import { useEffect, useMemo } from 'react'

import { useConnect } from 'redux-bundler-hook'

import { Box } from '@mui/material'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'

import logo from '@assets/logo.svg'
import logoBug from '@assets/logo_bug.svg'
import { Loading } from '@common/components'
import { useSmallScreen } from '@common/utils'

import { LeftDrawerButton, RightDrawerButton } from './DrawerButton'
import { Drawer, DrawerHeader, logoStyles, miniLogo } from './Sidebar.styles'
import getSidebarEntries from './sidebarEntries'
import SidebarMenuItem from './SidebarMenuItem'
import UserMenu from './UserMenu'

export default function Sidebar() {
  const isSmallScreen = useSmallScreen()
  let resizeTimeout

  const {
    me,
    routes,
    system,
    systemPortalRoles,
    currentRole,
    routeInfo: { url: currentURL },
    drawerOpen,
    drawerCollapsed,
    drawerScrollbarData,
    doCloseDrawer,
    doCollapseDrawer,
    doExpandDrawer,
    doUpdateDrawerScrollbarData,
  } = useConnect(
    'selectMe',
    'selectSystem',
    'selectRoutes',
    'selectSystemPortalRoles',
    'selectCurrentRole',
    'selectRouteInfo',
    'selectDrawerOpen',
    'selectDrawerCollapsed',
    'selectDrawerScrollbarData',
    'doCloseDrawer',
    'doCollapseDrawer',
    'doExpandDrawer',
    'doUpdateDrawerScrollbarData',
  )

  const portalRoles = useMemo(
    () =>
      systemPortalRoles
        ?.filter((role) => role.id !== 'INSTALLER')
        ?.map((role) => role.id) ?? [],
    [systemPortalRoles],
  )

  const sidebarEntries = useMemo(
    () => getSidebarEntries({ routes, portalRoles }),
    [routes, portalRoles],
  )

  const open = isSmallScreen.mobile ? drawerOpen : !drawerCollapsed

  const handleDrawer = () => {
    if (drawerCollapsed) {
      doExpandDrawer()
    } else {
      doCollapseDrawer()
    }
  }

  const onResize = () => {
    clearTimeout(resizeTimeout)
    resizeTimeout = setTimeout(() => {
      const drawerEl = document.getElementById('portal_drawer')
      const paperEl = drawerEl?.querySelector('.MuiDrawer-paper')
      const scrollVisible = paperEl?.scrollHeight > paperEl?.clientHeight
      if (drawerScrollbarData.visible !== scrollVisible) {
        doUpdateDrawerScrollbarData({
          visible: scrollVisible,
          width: scrollVisible ? paperEl.offsetWidth - paperEl.clientWidth : 0,
        })
      }
    }, 50)
  }

  useEffect(() => {
    globalThis.addEventListener('resize', onResize)
    onResize()
    return () => {
      globalThis.removeEventListener('resize', onResize)
      clearTimeout(resizeTimeout)
    }
  }, [drawerScrollbarData])

  if (!me && !system) {
    // we can do better than this
    return <Loading />
  }

  return (
    <Drawer
      id="portal_drawer"
      scrollbarData={drawerScrollbarData}
      variant={isSmallScreen.mobile ? 'temporary' : 'permanent'}
      anchor="left"
      open={open}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: 'background.dark',
            color: 'white',
            scrollbarWidth: 'thin',
          },
        },
      }}
      sx={{ direction: 'rtl' }}
      onClose={doCloseDrawer}
    >
      <Box
        sx={{
          direction: 'ltr',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <DrawerHeader>
          {open ? (
            <>
              <img src={logo} alt="Rest Potal" style={logoStyles} />
              {!isSmallScreen.mobile && (
                <LeftDrawerButton leftOpen={open} handleDrawer={handleDrawer} />
              )}
            </>
          ) : (
            <>
              <img src={logoBug} alt="Rest Potal" style={miniLogo} />
              {!isSmallScreen.mobile && (
                <RightDrawerButton
                  leftOpen={!open}
                  handleDrawer={handleDrawer}
                  parentScrollbarData={drawerScrollbarData}
                />
              )}
            </>
          )}
        </DrawerHeader>
        <List sx={{ '& .MuiListItemIcon-root': { minWidth: '39px' } }}>
          <Divider sx={{ mb: 1, backgroundColor: 'grey.500' }} />
          {sidebarEntries
            .filter((menuItem) =>
              menuItem.subitems
                ? menuItem.subitems.some(
                    (subitem) =>
                      subitem.allowedRoles.includes(currentRole) &&
                      subitem.hidden !== true,
                  )
                : menuItem.allowedRoles.includes(currentRole) &&
                  menuItem.hidden !== true,
            )
            .map((menuItem) => (
              <SidebarMenuItem
                key={menuItem.label}
                open={open}
                icon={menuItem.icon}
                label={menuItem.label}
                href={menuItem.href}
                subitems={menuItem.subitems?.filter(
                  (subitem) =>
                    subitem.allowedRoles.includes(currentRole) &&
                    subitem.hidden !== true,
                )}
                currentURL={currentURL}
              />
            ))}
        </List>
        <Box style={{ marginTop: 'auto' }}>
          <Divider sx={{ backgroundColor: 'grey.500' }} />
          <UserMenu open={open} me={me} />
        </Box>
      </Box>
    </Drawer>
  )
}
